import { useEffect, useRef, useState } from 'react';

import apiOffers from 'api/offers';
import { IOfferVisitor } from 'api/interfaces/offers/IOfferVisit';

// The server forgets a page that has not reported in 75 seconds
const REPORT_INTERVAL_MS = 30000;

/**
 * Reports the open offer page or position calculation every half minute and returns the
 * other users on it now. Nothing is reported without a target, e.g. for a new offer.
 */
const useOfferVisit = (
  target: {
    additionalOfferId?: number;
    offerId?: number;
    offerProductId?: number;
  },
  editing: boolean
): IOfferVisitor[] => {
  const [others, setOthers] = useState<IOfferVisitor[]>([]);
  const visitId = useRef<number>();
  // Read at every report, so e.g. a page locked after loading keeps its visit
  const editingNow = useRef(editing);
  editingNow.current = editing;
  const { additionalOfferId, offerId, offerProductId } = target;

  useEffect(() => {
    if (
      additionalOfferId === undefined &&
      offerId === undefined &&
      offerProductId === undefined
    ) {
      return undefined;
    }

    let stopped = false;
    visitId.current = undefined;

    const report = async () => {
      try {
        const result = await apiOffers.visits.put({
          additionalOfferId,
          editing: editingNow.current,
          offerId,
          offerProductId,
          visitId: visitId.current,
        });

        if (!stopped && result !== undefined) {
          visitId.current = result.visitId;
          // Only a change of the others renders the page again, not every report
          const newOthers = result.others ?? [];
          setOthers((current) =>
            JSON.stringify(current) === JSON.stringify(newOthers)
              ? current
              : newOthers
          );
        }
      } catch (error) {
        // The others are only a notice, the page works without them
      }
    };

    report();
    const interval = window.setInterval(report, REPORT_INTERVAL_MS);

    return () => {
      stopped = true;
      window.clearInterval(interval);
      if (visitId.current !== undefined) {
        apiOffers.visits.close(visitId.current).catch(() => undefined);
      }
    };
  }, [additionalOfferId, offerId, offerProductId]);

  return others;
};

export default useOfferVisit;
